import _ from 'underscore';
import {completions as autocompletions} from '@netx/core-app/properties/autocomplete';
import AttributesCollection from '@netx/core-asset/collections/attributes';
import AttributeModel from '@netx/core-asset/models/attribute';
import {dispatcher} from '@netx/core-next/instance/dispatcher';
import {queryClient} from '@netx/core-next/lib/queryClient';
import {
	getAttributeTemplates,
	GetAttributeTemplatesReactQueryKey,
} from '../api/react-query/useReactQueryGetAttributeTemplates';
import {useAttributesStore} from '../store/attributesStore';
import {AttributeTemplateObjectType} from '../schema/attributeTemplate';

export const attributes = new AttributesCollection([]);
export const attributeTemplates = attributes;

/**
 * Fetch attribute templates through the modern API/store instead of Backbone's
 * own `attributes.fetch()`. `useAttributesStore` is the source of truth for reads;
 * the result is mirrored down into the Backbone `attributes` collection below so
 * legacy code (including the Backbone attribute editor) keeps working unchanged.
 */
export const fetchAttributeTemplates = async () => {
	useAttributesStore
		.getState()
		.update(useAttributesStore.getState().attributes, true);

	return queryClient
		.fetchQuery({
			queryKey: [GetAttributeTemplatesReactQueryKey],
			queryFn: getAttributeTemplates,
		})
		.then((result) => {
			useAttributesStore.getState().update(result, false);
			return result;
		})
		.catch((error) => {
			useAttributesStore
				.getState()
				.update(useAttributesStore.getState().attributes, false);
			throw error;
		});
};

attributes.listenTo(
	dispatcher,
	'system:attribute:edit:success',
	(model: any, isNew: boolean) => {
		if (!isNew) return;
		// Add the model attributes so that we get the proper attribute model type
		// when creating a new attribute the model is just the generic base model
		attributes.add(model.attributes);
		attributes.sort();
	},
);

// Update autocompletions
const attributeHistoryCompletion = autocompletions.attributeHistory;
attributes.on(
	'add remove sync',
	_.debounce(() => {
		// @ts-ignore
		attributeHistoryCompletion.data = attributes
			.filterByHistory()
			.map((attr: AttributeModel) => ({
				label: attr.getLabel(),
				value: attr.id,
			}));
	}, 50),
);

// Keeping clip and asset attributes together in the same call with wholly different needs was
// kinf of a mistake
export const assetAttributes = attributes.filterByObjectType(
	AttributeTemplateObjectType.ASSET,
);
export const clipAttributes = attributes.filterByObjectType(
	AttributeTemplateObjectType.CLIP,
);

// Update our sub attribute collections
const attributesChanged = _.debounce(() => {
	assetAttributes.set(
		attributes.filterByObjectType(AttributeTemplateObjectType.ASSET).models,
		{
			merge: true,
		},
	);
	clipAttributes.set(
		attributes.filterByObjectType(AttributeTemplateObjectType.CLIP).models,
		{
			merge: true,
		},
	);
	dispatcher.trigger('asset:attributes:updated', attributes);
}, 25);

attributes
	.on('add remove', attributesChanged)
	.on('change', (model) => {
		!_.isEmpty(model.changed) && attributesChanged();
	})
	.on('sync', (collection) => {
		collection === attributes && attributesChanged();
	});

export const getAttribute = (id?: number, name?: string) => {
	if (id != null) return attributes.get(parseInt(id.toString()));
	if (name != null) return attributes.findWhere({name: name});
};

export const getAttributesByObjectType = (
	type: AttributeTemplateObjectType,
) => {
	return attributes.filterByObjectType(type);
};

export const getAttributeId = (name: string) => {
	return getAttribute(undefined, name)?.id;
};

// Bidirectional mirror between the Backbone collection and the store.
// `attributesStore` is the source of truth for reads (populated via
// `fetchAttributeTemplates` above); the Backbone attribute editor is still
// the source of truth for writes, mutating `attributes` directly. These two
// flags stop the two listeners from ping-ponging off each other - both fire
// synchronously within the same call stack, so a simple re-entrancy guard is
// sufficient (no async gap to race).
let applyingFromStore = false;
let applyingFromBackbone = false;

// Backbone -> store (writes: attribute create/edit from the legacy editor)
attributes.on('sync change reset add remove request:read', () => {
	if (applyingFromStore) return;
	applyingFromBackbone = true;
	useAttributesStore.getState().update(
		attributes.map((m) => ({...m.attributes})),
		!attributes.syncedOnce || attributes.syncing,
	);
	applyingFromBackbone = false;
});

// Store -> Backbone (reads: initial fetch and any future store-driven refetch)
useAttributesStore.subscribe((state) => {
	if (applyingFromBackbone) return;
	applyingFromStore = true;
	attributes.set(state.attributes);
	// `.set()` doesn't fire `sync` the way `.fetch()` does - some legacy code
	// (e.g. core-asset/controller.js) still listens for it, so trigger it
	// explicitly to preserve that behavior.
	attributes.trigger('sync', attributes);
	applyingFromStore = false;
});

export default attributes;

import {setGlobal} from '@netx/core-next/utils/setGlobal';
// circular dependency:
// attribute model (related to some metadata map stuff)
// attribute collection (related to some metadata map stuff)
// if (process.env.NODE_ENV !== 'production') {
setGlobal({attributes, assetAttributes, clipAttributes});
//}
